import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from 'components/CncUI/Typography';
import Button from 'components/CncUI/Button';
import Step from './Step';
import NextIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import cncLogo from 'images/CnCLogoRegister.svg';

import useMutation from 'hooks/useMutation';
import Logger from 'utilities/logger';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  root: {
    maxWidth: 1180,
    margin: 'auto',
    [breakpoints.up('md')]: {
      paddingTop: 94,
    },
  },
  leftPanel: {
    backgroundImage: palette.cncUI.gradients.header,
    boxShadow:
      '0px 1px 5px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.12)',
    [breakpoints.up('md')]: {
      height: 630,
    },
    [breakpoints.down('sm')]: {
      height: 'auto',
      paddingBottom: 20,
    },
  },
  rightPanel: {
    padding: '6px 0',
    [breakpoints.down('sm')]: {
      padding: 0,
    },
  },
  paper: {
    borderRadius: 0,
    padding: '16px 20px 24px',
    [breakpoints.up('md')]: {
      height: 506,
      padding: '110px 95px 0',
    },
  },
  innerLeft: {
    height: '100%',
    color: palette.cncUI.default['+10'],
    padding: '8px 16px',
    [breakpoints.down('sm')]: {
      maxWidth: 560,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    [breakpoints.up('md')]: {
      padding: 40,
    },
    '& h3': {
      [breakpoints.up('md')]: {
        marginTop: 60,
      },
      [breakpoints.down('sm')]: {
        textAlign: 'center',
        marginTop: 20,
      },
    },
    '& p': {
      [breakpoints.down('sm')]: {
        textAlign: 'center',
      },
    },
  },
  logo: {
    [breakpoints.down('sm')]: {
      textAlign: 'center',
    },
    '& img': {
      [breakpoints.up('sm')]: {
        marginLeft: 2,
        width: 340,
      },
      [breakpoints.down('sm')]: {
        width: 280,
        marginTop: 40,
      },
    },
  },
  steps: {
    alignItems: 'center',
    marginTop: 20,
    marginBottom: 60,
  },
  button: {
    borderRadius: 2,
    marginTop: 24,
    [breakpoints.down('sm')]: {
      marginTop: 0,
      marginBottom: 15,
    },
  },
  noHeight: {
    height: 0,
  },
  mobileExpand: {
    position: 'relative',
    backgroundColor: palette.cncUI.default['+6'],
    color: palette.cncUI.white,
    minHeight: 24,
    width: 24,
    height: 24,
    alignContent: 'center',
    justifyContent: 'center',
    top: -12,
    borderRadius: '50%',
    marginRight: 'auto',
    marginLeft: 'auto',
  },
  mfaText: {
    margin: 0,
    [breakpoints.down('xs')]: {
      maxWidth: 280,
    },
  },
  wrapper: {
    [breakpoints.only('sm')]: {
      width: 560,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  title: {
    [breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  subtitle: {
    marginTop: 12,
    [breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
}));

const Welcome = props => {
  const classes = useStyles();
  const { token, state, setStep, changeState, isMobile } = props;
  const [loading, setLoading] = useState(false);
  const validateActivationToken = useMutation('validateActivationToken', {
    request: token => ({ token }),
    response: res => res.data.validateActivationToken || {},
  });
  const logger = Logger('AccountCreation.Welcome');

  const validate = () => {
    setLoading(true);
    validateActivationToken(token)
      .then(tokenData => {
        if (tokenData.status === 'IDP_SKIP') {
          const uri = encodeURIComponent(
            tokenData.user && tokenData.user.email,
          );
          const fromUri = encodeURIComponent(state.fromURI);
          window.location.replace(
            `/login?login=${uri}&fromURI=${fromUri}&hide=register`,
          );
          return null;
        } else {
          changeState({ tokenData });
          setStep('CreatePassword');
        }
      })
      .catch(err => {
        logger.error(err.message);
        changeState({ error: err });
        setStep('Error');
      });
  };

  return (
    <>
      <Grid container spacing={0} className={classes.root} id="WelcomeRoot">
        <Grid item xs={12} md={6} className={classes.leftPanel}>
          <Grid
            container
            spacing={0}
            justifyContent="space-between"
            direction="column"
            className={classes.innerLeft}
          >
            <Grid item>
              <div className={classes.logo}>
                <img src={cncLogo} alt="CastandCrew Logo" />
              </div>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h3" white noMargin>
                    Account Activation
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" bold white noMargin>
                    Complete the security setup of your new MyCast&Crew account.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography size="long" white noMargin>
                    After activating your account, you’ll have the opportunity
                    to complete your profile if you haven’t registered yet.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography white size="long" noMargin>
                    Registered users will be able to login and begin using
                    MyCast&Crew.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {isMobile && (
          <Grid item xs={12} className={classes.noHeight}>
            <div className={classes.mobileExpand}>
              <ExpandMoreIcon />
            </div>
          </Grid>
        )}
        <Grid item xs={12} md={6} className={classes.rightPanel}>
          <Paper className={classes.paper}>
            <Grid container spacing={0} className={classes.wrapper}>
              <Grid item xs={12}>
                <Typography variant="h4" className={classes.title}>
                  Let’s secure your new account
                </Typography>
                <Typography variant="body2" className={classes.subtitle}>
                  To activate your account, you’ll need to
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={3} className={classes.steps}>
                  <Grid item xs={12}>
                    <Step index={1}>
                      <Typography variant="body2" medium>
                        Create your password
                      </Typography>
                    </Step>
                  </Grid>
                  <Grid item xs={12}>
                    <Step index={2}>
                      <Typography variant="body2" medium noMargin>
                        Add and verify your phone number
                      </Typography>
                      <Typography variant="caption" className={classes.mfaText}>
                        This enrolls you in our MFA service for added security
                      </Typography>
                    </Step>
                  </Grid>
                  <Grid item xs={12}>
                    <Step index={3}>
                      <Typography variant="body2" medium>
                        Confirm your preferences
                      </Typography>
                    </Step>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} className="welcome-button">
                <Button
                  fullWidth
                  loading={loading}
                  onClick={validate}
                  id="WelcomeNextButton"
                  endIcon={<NextIcon />}
                  className={classes.button}
                >
                  Get started
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default Welcome;
